import { useWatch, useFieldArray, type Control, type UseFormRegister, type FieldErrors } from "react-hook-form";
import { Input } from "@/components/ui/input";
import SelectFloating from '@/components/atoms/SelectFloating';
import {
    Card,
    CardContent
} from "@/components/ui/card";
import { type schemaType } from './schema';

interface Props {
    control: Control<schemaType>;
    register: UseFormRegister<schemaType>;
    errors: FieldErrors;
    index: number;
}

function OptionsFieldArray({ 
    control, 
    index,
    errors,
    register,
}: Props) {
    const watchedValue = useWatch({control, name: `projects.${index}.elements`});
    const { fields: optionFields } = useFieldArray({
      control,
      name: `projects.${index}.elements`,
    });

    const hasOptions = ({ index }:{ index: number }) => {
        if(!watchedValue) return false;

        const options = watchedValue[index]?.options;
        if(Array.isArray(options) && options.length > 0) {
            return true;
        }

        return false;
    }

    const getOptions = ({ index }:{ index: number }) => {
        if(!watchedValue) return [];

        const options = watchedValue[index]?.options;
        if(Array.isArray(options) && options.length > 0) {
            return options.map((option) => ({
                label: String(option.key),
                value: String(option.value),
            }));
        }

        return [];
    }

    return (
        <Card>
            <CardContent className="px-4 py-3 space-y-2">
            
                {optionFields.map((option, optionIndex) => (
                    <div key={option.id} className="flex gap-2">

                        <div className="flex-1 space-y-2">
                            <label className="text-sm font-medium leading-none">Element Name</label>
                            <div className="flex items-center w-full gap-2">
                                <Input disabled placeholder="Option Name" {...register(`projects.${index}.elements.${optionIndex}.name`)} />
                            </div>
                            {(errors?.projects as FieldErrors<schemaType>["projects"])?.[index]?.elements?.[optionIndex]?.name?.message && (
                                <p className="text-sm font-medium text-destructive">
                                    {String((errors?.projects as FieldErrors<schemaType>["projects"])?.[index]?.elements?.[optionIndex]?.name?.message)}
                                </p>
                            )}
                        </div>

                        <div className="flex-1 space-y-2">
                            { hasOptions({ index: optionIndex }) ? (
                                <SelectFloating
                                    label="Element Value"
                                    options={getOptions({ index: optionIndex })}
                                    showError
                                    errorMessage={(errors?.projects as FieldErrors<schemaType>["projects"])?.[index]?.elements?.[optionIndex]?.value?.message}
                                    name={`projects.${index}.elements.${optionIndex}.value`}
                                    control={control}
                                />
                            ) : (
                                <>
                                    <label className="text-sm font-medium leading-none">Element Value</label>
                                    <div className="flex items-center w-full gap-2">
                                        <Input placeholder="Type value..." {...register(`projects.${index}.elements.${optionIndex}.value`)} /> 
                                    </div>
                                    {(errors?.projects as FieldErrors<schemaType>["projects"])?.[index]?.elements?.[optionIndex]?.value?.message && (
                                        <p className="text-sm font-medium text-destructive">
                                            {String((errors?.projects as FieldErrors<schemaType>["projects"])?.[index]?.elements?.[optionIndex]?.value?.message)}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}

export { OptionsFieldArray };
